/**
 * HelpPanel — Help, FAQ, and feedback for the Outlook Add-in
 *
 * Ported from vanilla JS: 23_help.js
 */
import React, { useState, useCallback } from 'react';
import { Button } from '../../../components/ui/button';
import {
  X, HelpCircle, Search, ChevronDown, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const FAQ_ITEMS = [
  { q: 'How do I file an email?', a: 'Select an email in Outlook, search for the matter, select a subfolder, and click "File Email". The email will be saved to SharePoint.' },
  { q: 'What is File Like Last?', a: 'File Like Last lets you quickly file the current email to the same destination as your most recent filing. One click and done.' },
  { q: 'How do I add a favourite?', a: 'After selecting a folder, click the star icon next to the folder name to add it to your Favourites for quick access.' },
  { q: 'What does "Include Attachments" do?', a: 'When checked, email attachments are saved separately to the Documents subfolder in SharePoint, making them easier to find.' },
  { q: 'How do I record time?', a: 'Enable time recording in Settings (gear icon), then after selecting a matter, click "Record Time". Choose a template, adjust duration, and save to LawBillity.' },
  { q: 'What is the conversation filing?', a: 'After filing an email, you can choose to file the entire email conversation thread. This files all emails in the thread to the same location.' },
  { q: 'Why does it say "Already filed"?', a: 'Bonsai Mail checks if an email has already been filed to SharePoint. If it has, you\'ll see a green badge showing where it was filed.' },
  { q: 'Can I drag and drop files?', a: 'Yes! After selecting a destination folder, you can drag files from your desktop onto the Drop Zone to upload them directly to SharePoint.' },
];

export default function HelpPanel({ userId, bonsaiApi, onClose }) {
  const [activeSection, setActiveSection] = useState('faq');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [sendingFeedback, setSendingFeedback] = useState(false);

  const sendFeedback = useCallback(async () => {
    if (!feedback.trim()) return;
    setSendingFeedback(true);
    try {
      await bonsaiApi.post('/outlook/feedback', { message: feedback.trim(), source: 'outlook-addin' });
      toast.success('Feedback sent — thank you!');
      setFeedback('');
    } catch {
      toast.error('Failed to send feedback');
    }
    setSendingFeedback(false);
  }, [bonsaiApi, feedback]);

  return (
    <div className="absolute inset-0 bg-navy-card/95 z-50 flex flex-col overflow-hidden" data-testid="help-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <HelpCircle className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-semibold text-white">Help & Tools</span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="help-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Section tabs */}
      <div className="flex border-b border-gray-700/50 px-2 gap-1">
        {[
          { key: 'faq', label: 'FAQ', icon: HelpCircle },
          { key: 'feedback', label: 'Feedback', icon: MessageSquare },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveSection(t.key)}
            className={`flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-medium border-b-2 transition-colors ${
              activeSection === t.key ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-300'
            }`} data-testid={`help-tab-${t.key}`}>
            <t.icon className="w-3 h-3" />{t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {/* FAQ */}
        {activeSection === 'faq' && (
          <div className="space-y-1" data-testid="help-faq">
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} className="border border-gray-700/50 rounded" data-testid={`faq-item-${i}`}>
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full text-left px-3 py-2 flex items-center justify-between text-xs text-gray-200 hover:bg-gray-800/40 transition-colors">
                  <span>{item.q}</span>
                  <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${expandedFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {expandedFaq === i && (
                  <div className="px-3 pb-2 text-[11px] text-gray-400 border-t border-gray-700/30">{item.a}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Feedback */}
        {activeSection === 'feedback' && (
          <div className="space-y-3" data-testid="help-feedback">
            <p className="text-xs text-gray-400">Share feedback, report bugs, or suggest improvements.</p>
            <textarea value={feedback} onChange={e => setFeedback(e.target.value)}
              rows={4} placeholder="Your feedback..."
              className="w-full rounded border border-gray-700 bg-gray-800 text-xs text-white p-2 resize-none focus:border-blue-600 focus:outline-none"
              data-testid="feedback-textarea" />
            <Button onClick={sendFeedback} disabled={sendingFeedback || !feedback.trim()} size="sm"
              className="w-full bg-blue-600 hover:bg-blue-500 text-xs h-8" data-testid="feedback-send-btn">
              {sendingFeedback ? 'Sending...' : 'Send Feedback'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
